'use client';

import React, {
  useCallback, useEffect, useRef, useState
} from 'react';

import styles from './kanpaigame.module.scss';

// Canvas can't read SCSS variables, so map them here:
// $pw-white-lighter = #e6e6e6, $pw-black-darker = #1a1a1a
// $pw-purple-dark = #1D1638, destructive button = #DB4F4F
// $agent-primary = #2563eb

type SakeItem = {
  x: number;
  y: number;
  speed: number;
  type: 'sake';
  wobble: number;
  wobbleSpeed: number;
};

type BugItem = {
  x: number;
  y: number;
  speed: number;
  type: 'bug';
  wobble: number;
  wobbleSpeed: number;
  legPhase: number;
};

type Item = SakeItem | BugItem;

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: string;
  size: number;
};

type Dog = {
  x: number;
  y: number;
  w: number;
  h: number;
  speed: number;
  facing: number;
  tilt: number;
  squash: number;
  catchFlash: number;
};

type GameState = {
  W: number;
  H: number;
  dog: Dog;
  items: Item[];
  particles: Particle[];
  score: number;
  lives: number;
  spawnTimer: number;
  spawnInterval: number;
  difficulty: number;
  frame: number;
  combo: number;
  comboTimer: number;
  shake: number;
  running: boolean;
};

type Screen = 'start' | 'playing' | 'over';

const KanpaiGame = () => {
  const canvasRef = useRef<HTMLCanvasElement>( null );
  const containerRef = useRef<HTMLDivElement>( null );
  const gameRef = useRef<GameState | null>( null );
  const keysRef = useRef<Record<string, boolean>>( {} );
  const animRef = useRef<number>( 0 );
  const dogImgRef = useRef<HTMLImageElement | null>( null );
  const dogFlippedRef = useRef<HTMLCanvasElement | null>( null );
  const bgCacheRef = useRef<HTMLCanvasElement | null>( null );

  const [ screen, setScreen ] = useState<Screen>( 'start' );
  const [ finalScore, setFinalScore ] = useState( 0 );

  // Load dog image once
  useEffect( () => {
    const img = new Image();
    img.src = 'images/kanpai_dog.png';
    img.onload = () => {
      dogImgRef.current = img;
      const flipped = document.createElement( 'canvas' );
      flipped.width = img.width;
      flipped.height = img.height;
      const fc = flipped.getContext( '2d' );
      if ( fc ) {
        fc.translate( img.width, 0 );
        fc.scale( -1, 1 );
        fc.drawImage( img, 0, 0 );
      }
      dogFlippedRef.current = flipped;
    };
  }, [] );

  // Key handlers
  useEffect( () => {
    const down = ( e: KeyboardEvent ) => { keysRef.current[ e.key ] = true; };
    const up = ( e: KeyboardEvent ) => { keysRef.current[ e.key ] = false; };
    window.addEventListener( 'keydown', down );
    window.addEventListener( 'keyup', up );
    return () => {
      window.removeEventListener( 'keydown', down );
      window.removeEventListener( 'keyup', up );
    };
  }, [] );

  const buildBgCache = useCallback( ( W: number, H: number ) => {
    const offscreen = document.createElement( 'canvas' );
    offscreen.width = W;
    offscreen.height = H;
    const oc = offscreen.getContext( '2d' );
    if ( !oc ) return offscreen;

    oc.fillStyle = '#e6e6e6'; // $pw-white-lighter
    oc.fillRect( 0, 0, W, H );

    oc.fillStyle = 'rgba(0, 0, 0, 0.04)';
    for ( let gx = 20; gx < W; gx += 30 ) {
      for ( let gy = 20; gy < H; gy += 30 ) {
        oc.beginPath();
        oc.arc( gx, gy, 1, 0, Math.PI * 2 );
        oc.fill();
      }
    }

    return offscreen;
  }, [] );

  const spawnItem = useCallback( ( g: GameState ): void => {
    const isBug = Math.random() < 0.35;
    const base = {
      x: 30 + Math.random() * ( g.W - 60 ),
      y: -30,
      speed: 2.2 + Math.random() * 1.5 + g.difficulty * 0.4,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.03 + Math.random() * 0.02
    };

    if ( isBug ) {
      g.items.push( { ...base, type: 'bug', legPhase: Math.random() * Math.PI * 2 } );
    } else {
      g.items.push( { ...base, type: 'sake' } );
    }
  }, [] );

  const addParticles = useCallback( ( g: GameState, x: number, y: number, color: string, count: number ) => {
    for ( let i = 0; i < count; i++ ) {
      g.particles.push( {
        x, y,
        vx: ( Math.random() - 0.5 ) * 8,
        vy: ( Math.random() - 0.5 ) * 6,
        life: 20 + Math.random() * 15,
        maxLife: 35,
        color,
        size: 2 + Math.random() * 3
      } );
    }
  }, [] );

  // ---- Drawing helpers ----

  const drawDog = useCallback( ( ctx: CanvasRenderingContext2D, g: GameState ) => {
    const d = g.dog;
    ctx.save();
    ctx.translate( d.x, d.y );
    const bob = Math.sin( g.frame * 0.08 ) * 2;
    ctx.translate( 0, bob );
    ctx.rotate( d.tilt );
    const sx = 1 + ( d.squash - 1 ) * 0.3;
    const sy = 1 - ( d.squash - 1 ) * 0.3;
    ctx.scale( sx, sy );

    ctx.fillStyle = 'rgba(0,0,0,0.15)';
    ctx.beginPath();
    ctx.ellipse( 0, d.h / 2 + 3, d.w / 2.2, 5, 0, 0, Math.PI * 2 );
    ctx.fill();

    if ( d.catchFlash > 0 ) {
      ctx.shadowColor = '#e6a700';
      ctx.shadowBlur = d.catchFlash * 2;
    }

    const img = d.facing < 0 ? dogFlippedRef.current : dogImgRef.current;
    if ( img ) {
      ctx.drawImage( img, -d.w / 2, -d.h / 2, d.w, d.h );
    }

    ctx.shadowBlur = 0;
    ctx.restore();
  }, [] );

  const drawSake = useCallback( ( ctx: CanvasRenderingContext2D, item: SakeItem, frame: number ) => {
    ctx.save();
    ctx.translate( item.x, item.y );
    const sway = Math.sin( item.wobble ) * 2;
    ctx.translate( 0, sway );
    const s = 0.85;

    // Cup shape
    ctx.beginPath();
    ctx.moveTo( -14 * s, -12 * s );
    ctx.bezierCurveTo( -15 * s, -10 * s, -13 * s, 8 * s, -7 * s, 12 * s );
    ctx.lineTo( 7 * s, 12 * s );
    ctx.bezierCurveTo( 13 * s, 8 * s, 15 * s, -10 * s, 14 * s, -12 * s );
    ctx.closePath();

    const cupGrad = ctx.createLinearGradient( -14 * s, 0, 14 * s, 0 );
    cupGrad.addColorStop( 0, '#e8e4dc' );
    cupGrad.addColorStop( 0.3, '#faf8f3' );
    cupGrad.addColorStop( 0.7, '#faf8f3' );
    cupGrad.addColorStop( 1, '#d9d4c8' );
    ctx.fillStyle = cupGrad;
    ctx.fill();
    ctx.strokeStyle = '#8b8578';
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Blue wave pattern
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = '#2563eb'; // $agent-primary
    ctx.lineWidth = 1.5;
    for ( let row = 0; row < 2; row++ ) {
      const baseY = 2 * s + row * 5 * s;
      ctx.beginPath();
      for ( let wx = -16 * s; wx < 16 * s; wx += 6 * s ) {
        ctx.moveTo( wx, baseY );
        ctx.quadraticCurveTo( wx + 3 * s, baseY - 3 * s, wx + 6 * s, baseY );
      }
      ctx.stroke();
    }
    ctx.fillStyle = '#2563eb'; // $agent-primary
    ctx.beginPath();
    ctx.arc( 0, 8 * s, 3 * s, 0, Math.PI * 2 );
    ctx.fill();
    ctx.restore();

    // Liquid surface
    ctx.fillStyle = 'rgba(255, 252, 240, 0.7)';
    ctx.beginPath();
    ctx.ellipse( 0, -9 * s, 12 * s, 3 * s, 0, 0, Math.PI * 2 );
    ctx.fill();

    // Highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.beginPath();
    ctx.ellipse( -3 * s, -10 * s, 5 * s, 1.5 * s, -0.2, 0, Math.PI * 2 );
    ctx.fill();

    // Steam
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.07)';
    ctx.lineWidth = 1;
    const t = frame * 0.05 + item.wobble;
    for ( let i = 0; i < 3; i++ ) {
      const stx = -6 + i * 6;
      const phase = t + i * 2;
      ctx.beginPath();
      ctx.moveTo( stx * s, -14 * s );
      ctx.quadraticCurveTo( stx * s + Math.sin( phase ) * 4, -20 * s, stx * s + Math.sin( phase + 1 ) * 3, -26 * s );
      ctx.stroke();
    }

    ctx.restore();
  }, [] );

  const drawBug = useCallback( ( ctx: CanvasRenderingContext2D, item: BugItem ) => {
    ctx.save();
    ctx.translate( item.x, item.y );
    ctx.rotate( Math.sin( item.wobble ) * 0.3 );
    const s = 0.8;
    const legAnim = Math.sin( item.legPhase ) * 0.3;

    // Legs
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    for ( const side of [ -1, 1 ] ) {
      for ( let i = 0; i < 3; i++ ) {
        const baseY = -6 * s + i * 7 * s;
        const phase = legAnim * ( i % 2 === 0 ? 1 : -1 ) * side;
        ctx.beginPath();
        ctx.moveTo( side * 8 * s, baseY );
        ctx.lineTo( side * 12 * s, baseY + phase * 3 );
        ctx.lineTo( side * 17 * s, baseY + 4 * s + phase * 2 );
        ctx.stroke();
      }
    }

    // Abdomen
    const abdGrad = ctx.createRadialGradient( 0, 4 * s, 2 * s, 0, 4 * s, 12 * s );
    abdGrad.addColorStop( 0, '#7c2d12' );
    abdGrad.addColorStop( 0.5, '#991b1b' );
    abdGrad.addColorStop( 1, '#6b1010' );
    ctx.fillStyle = abdGrad;
    ctx.beginPath();
    ctx.ellipse( 0, 4 * s, 10 * s, 12 * s, 0, 0, Math.PI * 2 );
    ctx.fill();
    ctx.strokeStyle = '#4a0e0e';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Wing split
    ctx.strokeStyle = '#4a0e0e';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo( 0, -7 * s );
    ctx.lineTo( 0, 15 * s );
    ctx.stroke();

    // Wing markings
    ctx.strokeStyle = 'rgba(74, 14, 14, 0.5)';
    ctx.lineWidth = 1;
    for ( let i = 0; i < 3; i++ ) {
      const cy = -2 * s + i * 6 * s;
      ctx.beginPath();
      ctx.moveTo( -2 * s, cy );
      ctx.lineTo( -5 * s, cy + 2 * s );
      ctx.lineTo( -2 * s, cy + 4 * s );
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo( 2 * s, cy );
      ctx.lineTo( 5 * s, cy + 2 * s );
      ctx.lineTo( 2 * s, cy + 4 * s );
      ctx.stroke();
    }

    // Highlight
    ctx.fillStyle = 'rgba(255, 150, 100, 0.15)';
    ctx.beginPath();
    ctx.ellipse( -4 * s, 0, 4 * s, 7 * s, 0.15, 0, Math.PI * 2 );
    ctx.fill();

    // Thorax
    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.ellipse( 0, -9 * s, 7 * s, 4 * s, 0, 0, Math.PI * 2 );
    ctx.fill();

    // Head
    const headGrad = ctx.createRadialGradient( 0, -15 * s, 1 * s, 0, -15 * s, 6 * s );
    headGrad.addColorStop( 0, '#2d2d44' );
    headGrad.addColorStop( 1, '#1a1a2e' );
    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.ellipse( 0, -15 * s, 6 * s, 5 * s, 0, 0, Math.PI * 2 );
    ctx.fill();

    // Antennae
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 1.5;
    for ( const side of [ -1, 1 ] ) {
      const wave = Math.sin( item.wobble * 2 + side ) * 2;
      ctx.beginPath();
      ctx.moveTo( side * 3 * s, -19 * s );
      ctx.quadraticCurveTo( side * 10 * s + wave, -25 * s, side * 8 * s + wave, -30 * s );
      ctx.stroke();
      ctx.fillStyle = '#991b1b';
      ctx.beginPath();
      ctx.arc( side * 8 * s + wave, -30 * s, 1.5 * s, 0, Math.PI * 2 );
      ctx.fill();
    }

    // Eyes
    ctx.fillStyle = '#ff6b6b';
    ctx.shadowColor = '#ff0000';
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.ellipse( -3 * s, -16 * s, 2.5 * s, 2 * s, -0.2, 0, Math.PI * 2 );
    ctx.ellipse( 3 * s, -16 * s, 2.5 * s, 2 * s, 0.2, 0, Math.PI * 2 );
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.arc( -3 * s, -16 * s, 1.2 * s, 0, Math.PI * 2 );
    ctx.arc( 3 * s, -16 * s, 1.2 * s, 0, Math.PI * 2 );
    ctx.fill();

    // Mandibles
    ctx.strokeStyle = '#4a0e0e';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo( -2 * s, -11 * s );
    ctx.quadraticCurveTo( -4 * s, -9 * s, -2 * s, -8 * s );
    ctx.moveTo( 2 * s, -11 * s );
    ctx.quadraticCurveTo( 4 * s, -9 * s, 2 * s, -8 * s );
    ctx.stroke();

    ctx.restore();
  }, [] );

  // ---- Game loop ----

  const startGame = useCallback( () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if ( !canvas || !container ) return;

    // Stop any existing game loop before starting a new one
    const prev = gameRef.current;
    if ( prev ) prev.running = false;
    if ( animRef.current ) cancelAnimationFrame( animRef.current );

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext( '2d' );
    if ( !ctx ) return;

    bgCacheRef.current = buildBgCache( rect.width, rect.height );

    const g: GameState = {
      W: rect.width,
      H: rect.height,
      dog: { x: rect.width / 2, y: rect.height - 40, w: 48, h: 48, speed: 6, facing: 0, tilt: 0, squash: 1, catchFlash: 0 },
      items: [],
      particles: [],
      score: 0,
      lives: 3,
      spawnTimer: 0,
      spawnInterval: 40,
      difficulty: 1,
      frame: 0,
      combo: 0,
      comboTimer: 0,
      shake: 0,
      running: true
    };
    gameRef.current = g;
    setScreen( 'playing' );

    const keys = keysRef.current;

    const update = () => {
      const d = g.dog;
      g.frame++;

      let moving = 0;
      if ( keys[ 'ArrowLeft' ] || keys[ 'a' ] || keys[ 'A' ] ) { d.x -= d.speed; moving = -1; }
      if ( keys[ 'ArrowRight' ] || keys[ 'd' ] || keys[ 'D' ] ) { d.x += d.speed; moving = 1; }
      d.x = Math.max( d.w / 2, Math.min( g.W - d.w / 2, d.x ) );

      if ( moving !== 0 ) d.facing = moving;

      const targetTilt = moving * 0.15;
      d.tilt += ( targetTilt - d.tilt ) * 0.15;
      d.squash += ( 1 - d.squash ) * 0.12;
      if ( d.catchFlash > 0 ) d.catchFlash -= 0.8;

      g.difficulty = 1 + g.score / 40;
      g.spawnInterval = Math.max( 12, 40 - g.difficulty * 3 );

      g.spawnTimer++;
      if ( g.spawnTimer >= g.spawnInterval ) {
        g.spawnTimer = 0;
        spawnItem( g );
      }

      g.items.forEach( item => {
        item.y += item.speed;
        item.wobble += item.wobbleSpeed;
        if ( item.type === 'bug' ) item.legPhase += 0.15;
      } );

      const hitR = 24;
      g.items = g.items.filter( item => {
        if ( item.y > g.H + 40 ) {
          if ( item.type === 'sake' ) g.combo = 0;
          return false;
        }

        const dx = Math.abs( item.x - d.x );
        const dy = Math.abs( item.y - d.y );

        if ( dx < hitR && dy < hitR ) {
          if ( item.type === 'sake' ) {
            g.combo++;
            g.comboTimer = 60;
            g.score += 10 * Math.min( g.combo, 5 );
            d.squash = 1.35;
            d.catchFlash = 12;
            addParticles( g, item.x, item.y, 'rgba(230, 167, 0, 1)', 10 );
          } else {
            g.lives--;
            g.shake = 10;
            g.combo = 0;
            d.squash = 0.65;
            addParticles( g, item.x, item.y, 'rgba(219, 79, 79, 1)', 12 ); // destructive red
            if ( g.lives <= 0 ) {
              g.running = false;
              setFinalScore( g.score );
              setScreen( 'over' );
            }
          }
          return false;
        }
        return true;
      } );

      if ( g.comboTimer > 0 ) g.comboTimer--;
      if ( g.shake > 0 ) g.shake -= 0.8;

      g.particles.forEach( p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.96;
        p.vy *= 0.96;
        p.life--;
      } );
      g.particles = g.particles.filter( p => p.life > 0 );
    };

    const render = () => {
      ctx.save();
      if ( g.shake > 0 ) {
        ctx.translate(
          ( Math.random() - 0.5 ) * g.shake,
          ( Math.random() - 0.5 ) * g.shake
        );
      }

      // Pre-rendered background with dot grid
      const bg = bgCacheRef.current;
      if ( bg ) {
        ctx.drawImage( bg, 0, 0 );
      }

      // Items
      g.items.forEach( item => {
        if ( item.type === 'sake' ) drawSake( ctx, item, g.frame );
        else drawBug( ctx, item );
      } );

      // Particles
      g.particles.forEach( p => {
        const alpha = p.life / p.maxLife;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc( p.x, p.y, p.size * alpha, 0, Math.PI * 2 );
        ctx.fill();
      } );
      ctx.globalAlpha = 1;

      drawDog( ctx, g );

      // HUD
      ctx.fillStyle = '#1a1a1a'; // $pw-black-darker
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText( `Score: ${ g.score }`, 10, 20 );

      if ( g.combo > 1 && g.comboTimer > 0 ) {
        ctx.globalAlpha = g.comboTimer / 60;
        ctx.fillText( `x${ g.combo }`, 10, 36 );
        ctx.globalAlpha = 1;
      }

      // Lives
      ctx.textAlign = 'right';
      ctx.fillStyle = '#DB4F4F'; // destructive red
      ctx.font = '14px system-ui, sans-serif';
      let hearts = '';
      for ( let i = 0; i < g.lives; i++ ) hearts += '\u2764 ';
      ctx.fillText( hearts, g.W - 10, 20 );

      ctx.restore();
    };

    const loop = () => {
      if ( !g.running ) return;
      update();
      render();
      animRef.current = requestAnimationFrame( loop );
    };

    loop();
  }, [ spawnItem, addParticles, buildBgCache, drawDog, drawSake, drawBug ] );

  // Handle container resize
  useEffect( () => {
    const container = containerRef.current;
    if ( !container ) return;

    const observer = new ResizeObserver( () => {
      const g = gameRef.current;
      const canvas = canvasRef.current;
      if ( !g || !g.running || !canvas ) return;

      const rect = container.getBoundingClientRect();
      g.W = rect.width;
      g.H = rect.height;
      g.dog.y = g.H - 40;
      g.dog.x = Math.max( g.dog.w / 2, Math.min( g.W - g.dog.w / 2, g.dog.x ) );
      canvas.width = g.W;
      canvas.height = g.H;
      bgCacheRef.current = buildBgCache( g.W, g.H );
    } );

    observer.observe( container );
    return () => observer.disconnect();
  }, [ buildBgCache ] );

  // Cleanup on unmount
  useEffect( () => {
    return () => {
      if ( gameRef.current ) gameRef.current.running = false;
      if ( animRef.current ) cancelAnimationFrame( animRef.current );
    };
  }, [] );

  return (
    <div ref={ containerRef } className={ styles.container }>
      <canvas ref={ canvasRef } className={ styles.canvas } />

      { screen === 'start' && (
        <div className={ styles.overlay }>
          <img className={ styles.overlay_logo } src='images/kanpai_dog.png' alt='KanpAI™' />
          <span className={ styles.overlay_title }>KanpAI™ Catcher</span>
          <span className={ styles.overlay_subtitle }>Catch sake cups, dodge the bugs!</span>
          <button className={ styles.overlay_btn } onClick={ startGame }>Play</button>
          <span className={ styles.overlay_hint }>Arrow keys or A/D to move</span>
        </div>
      ) }

      { screen === 'over' && (
        <div className={ styles.overlay }>
          <span className={ styles.overlay_title }>Game Over</span>
          <span className={ styles.overlay_subtitle }>Score</span>
          <span className={ styles.overlay_score }>{ finalScore }</span>
          <button className={ styles.overlay_btn } onClick={ startGame }>Play Again</button>
        </div>
      ) }
    </div>
  );
};

export { KanpaiGame };
